import {
  Card,
  FlatActionList,
  Text,
  Page,
  Stack,
  Divider,
  Button,
  useFlashMessages,
} from "@towersystems/roam-common-native";
import { useTranslation } from "../../../../utilities/i18n";
import { useFormContext } from "react-hook-form";
import { StartStockCountFormValues } from "../../components/StartStockCountForm/types";
import {
  StockCount,
  StockCountSource,
} from "@towersystems/roam-common/lib/generated-types";
import {
  StartStockCountRootParamList,
  StockCountsRootParamList,
} from "../../../../types";
import { StackScreenProps } from "@react-navigation/stack";
import { LocationIdToName } from "../../../../components";
import { StartStockCountForm } from "../../components/StartStockCountForm";
import colors from "react-native-ui-lib/src/style/colors";
import React from "react";
import { useMutation } from "@apollo/client";
import { MUTATION_CREATE_STOCK_COUNT } from "../../../../utilities/stock-count";

export type StartStockCountScreenProps = StackScreenProps<
  StartStockCountRootParamList & StockCountsRootParamList,
  "StartStockCountScreen"
>;

const textButtonProps = {
  fontWeight: "bold",
  color: "action",
  variant: "body",
} as any;

export const StartStockCountScreen = ({
  navigation,
}: StartStockCountScreenProps) => {
  const { t } = useTranslation("stock_counts");

  const { watch, setError, formState, handleSubmit } =
    useFormContext<StartStockCountFormValues>();
  const flashMessagesContext = useFlashMessages();

  const [m] = useMutation<{
    createStockCount: StockCount;
  }>(MUTATION_CREATE_STOCK_COUNT);

  const watchedSource = watch("source");
  const watchedLocationId = watch("locationId");

  const { isDirty, isLoading, isSubmitting, isValid, isSubmitSuccessful } =
    formState;

  const handleOnSubmit = React.useCallback(
    async (input: StartStockCountFormValues) => {
      return await m({
        variables: {
          input,
        },
      })
        .then((result) => {
          if (result.data && !result.errors) {
            navigation.reset({
              index: 0,
              routes: [
                { name: "StockCountsScreen" },
                {
                  name: "PerformStockCountRootScreen",
                  params: {
                    stockCountId: result.data.createStockCount.id,
                  },
                },
              ],
            });
          } else {
            throw new Error(t("common:error"));
          }
        })
        .catch((err) => {
          flashMessagesContext.showMessage({
            message: err.message ?? t("common:error"),
            severity: "error",
          });
          setError("root.serverError", {
            type: "400",
            message: t("common:error"),
          });
        });
    },
    []
  );

  const sourceMarkup =
    watchedSource == StockCountSource.INTERNAL ? (
      <Card>
        <FlatActionList
          items={[
            {
              key: "source",
              title: t("form.source_internal.label"),
              subTitle: t("form.source_internal.description"),
              componentRight: () => {
                return <Text>Internal (Roam)</Text>;
              },
            },
          ]}
        />
      </Card>
    ) : undefined;

  const detailsMarkup = (
    <Card>
      <Stack vertical>
        <FlatActionList
          items={[
            {
              key: "location",
              title: t("form.location.label"),
              subTitle: t("form.location.description"),
              onPress: () => navigation.navigate("SelectLocationModalScreen"),
              componentRight: () =>
                watchedLocationId ? (
                  <LocationIdToName
                    locationId={watchedLocationId}
                    {...textButtonProps}
                  />
                ) : (
                  <Text {...textButtonProps}>Select Location</Text>
                ),
            },
          ]}
        />
        <Divider marginVertical={0} />
        <StartStockCountForm.NameFormInput />
      </Stack>
    </Card>
  );
  const disabled =
    !isDirty || isLoading || isSubmitting || !isValid || isSubmitSuccessful;

  const footerMarkup = (
    <Button
      disabled={disabled}
      label={t("button.start_count")}
      backgroundColor={colors.action}
      color="white"
      onPress={handleSubmit(handleOnSubmit)}
    ></Button>
  );

  return (
    <Page title={t("start_stock_count_title")} footer={footerMarkup}>
      <Stack vertical>
        {sourceMarkup}
        {detailsMarkup}
      </Stack>
    </Page>
  );
};
